import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MailIcon } from "@heroicons/react/outline";
import { AuthContext } from "context/Auth";
import { PrimaryButton } from "components/Button/PrimaryButton";
import { SupportDialog } from "./SupportDialog";

const SupportContext = createContext<{
  available: boolean;
  footerHeight: number;
  isOpen: boolean;
  open: () => void;
} | null>(null);

export const SupportProvider = ({ children }: PropsWithChildren) => {
  const { isAuthenticated, getUserInfo } = useContext(AuthContext);
  const { pathname } = useLocation();
  const user = getUserInfo();
  const available = isAuthenticated && !!user.accountId;
  const session = JSON.stringify([
    isAuthenticated,
    user.accountId,
    user.sub,
    pathname,
  ]);
  const [footerHeight, setFooterHeight] = useState(80);

  useEffect(() => {
    const footer = document.querySelector("footer");
    const measure = () =>
      setFooterHeight(footer?.getBoundingClientRect().height || 0);
    measure();
    const observer =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(measure)
        : undefined;
    if (footer) observer?.observe(footer);
    window.addEventListener("resize", measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, []);

  const [openSession, setOpenSession] = useState<string | null>(null);
  const isOpen = available && openSession === session;

  useEffect(() => {
    setOpenSession(null);
  }, [session]);

  return (
    <SupportContext.Provider
      value={{
        available,
        footerHeight,
        isOpen,
        open: () => setOpenSession(session),
      }}
    >
      {children}
      {isOpen && (
        <SupportDialog
          key={session}
          accountId={user.accountId}
          pagePath={pathname}
          onClose={() => setOpenSession(null)}
        />
      )}
    </SupportContext.Provider>
  );
};

export const useSupportFooterHeight = () =>
  useContext(SupportContext)?.footerHeight ?? 80;

const SupportTrigger = () => {
  const support = useContext(SupportContext);
  const { t } = useTranslation();
  if (!support?.available || support.isOpen) return null;

  return (
    <PrimaryButton onClick={support.open}>
      <span className="inline-flex items-center gap-2">
        <MailIcon className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
        {t("support.title")}
      </span>
    </PrimaryButton>
  );
};

export const SidebarSupportButton = () => (
  <div className="grid w-full">
    <SupportTrigger />
  </div>
);

export const SupportButton = () => {
  const support = useContext(SupportContext);

  if (!support?.available || support.isOpen) return null;

  return (
    <div
      className="fixed right-5 z-10 rounded-md shadow-lg lg:hidden"
      style={{ bottom: support.footerHeight + 20 }}
    >
      <SupportTrigger />
    </div>
  );
};
